import { useEffect, useRef, useState } from 'react'
import { InteractiveCanvas } from './canvas/InteractiveCanvas.tsx'
import { AssetPanelWrapper } from './components/AssetPanelWrapper'
import { UploadUrlApiError, designApi } from '../infrastructure/api/designApi'
import { calculateImageDPI, isAcceptableImageQuality, pxToMm } from '../domain/shared/units'
import { useDesignStore } from '../stores/designStore'
import { useCanvasStore } from '../stores/canvasStore'
import { useAssetPanelStore } from '../stores/assetPanelStore'
import { useTextDefaultsStore } from '../stores/textDefaultsStore'
import { assetRegistry } from '../infrastructure/services/assetRegistry'
import { transformOperations } from '../domain/canvas/value-objects/transform'
import type { CreateDesignResponse } from '../infrastructure/api/designApi'
import { Toolbar } from './components/Toolbar/Toolbar.tsx'
import { ImageQualityWarning } from './components/ImageQualityWarning'
import { ImageEditPanel } from './components/ImageEditPanel'
import { useIntlayer } from 'react-intlayer'
import { FloatingActionBar } from './components/FloatingActionBar'
import { OnboardingTour, useOnboardingTour } from './onboarding'
import { useToast } from '../hooks/useToast'
import * as Sentry from '@sentry/react'
import { TextEditor } from './components/TextEditor.tsx'
import { AIChatPanel } from './ai-chat'
import { Sparkles } from 'lucide-react'
import { useAIChatbotEnabled } from '../shared/flags'
import { useValidateLoadedDesignFonts } from './hooks/useValidateLoadedDesignFonts'

type DesignStudioEditorProps = {
  design: CreateDesignResponse
}

/**
 * Design Studio Editor
 * Initializes stores and renders the canvas
 */
export const DesignStudioEditor = ({ design }: DesignStudioEditorProps) => {
  const intl = useIntlayer('designStudioEditor')
  const { showToast } = useToast()
  const [isMobile, setIsMobile] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [isAIChatOpen, setIsAIChatOpen] = useState(false)
  const isAIChatbotEnabled = useAIChatbotEnabled()
  const { shouldShowTour, markTourCompleted, startTour } = useOnboardingTour()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [showTextEditor, setShowTextEditor] = useState(false)
  const [editingTextId, setEditingTextId] = useState<string | null>(null)
  const [replacingImageId, setReplacingImageId] = useState<string | null>(null)
  const [showImageEditor, setShowImageEditor] = useState(false)
  const [editingImageId, setEditingImageId] = useState<string | null>(null)
  const [imageQualityWarning, setImageQualityWarning] = useState<{
    show: boolean
    imageName: string
    dpi: number
  }>({ show: false, imageName: '', dpi: 0 })
  const [textContent, setTextContent] = useState('')
  const [textFont, setTextFont] = useState('Arial')
  const [textFontWeight, setTextFontWeight] = useState(400)
  const [textFontItalic, setTextFontItalic] = useState(false)
  const [textSize, setTextSize] = useState(16)
  const setElements = useCanvasStore((state) => state.setElements)
  const setLoadingState = useCanvasStore((state) => state.setLoadingState)
  const addTextElement = useCanvasStore((state) => state.addTextElement)
  const addImageElement = useCanvasStore((state) => state.addImageElement)
  const updateTextContent = useCanvasStore((state) => state.updateTextContent)
  const updateTextFont = useCanvasStore((state) => state.updateTextFont)
  const updateTextFontSize = useCanvasStore((state) => state.updateTextFontSize)
  const updateImageElement = useCanvasStore((state) => state.updateImageElement)
  const resetCanvas = useCanvasStore((state) => state.reset)
  const resetDesign = useDesignStore((state) => state.reset)
  const openAssetPanel = useAssetPanelStore((state) => state.openPanel)
  const currentDesign = useDesignStore((state) => state.currentDesign)
  const setModalOpen = useCanvasStore((state) => state.setModalOpen)
  const elements = useCanvasStore((state) => state.elements)
  const isSaving = useDesignStore((state) => state.isSaving)
  const isAddingToCart = useDesignStore((state) => state.isAddingToCart)
  const setAddingToCart = useDesignStore((state) => state.setAddingToCart)

  // Strip unsupported characters from text elements once a design's fonts are loaded.
  useValidateLoadedDesignFonts()

  // Check for edit mode from URL query parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const mode = urlParams.get('mode')
    setIsEditMode(mode === 'edit')
  }, [])

  // Update modal state when text editor or image editor opens/closes
  useEffect(() => {
    setModalOpen(showTextEditor || showImageEditor)
  }, [showTextEditor, showImageEditor, setModalOpen])

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768) // md breakpoint
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Listen for add to cart loading state from parent
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'ADD_TO_CART_LOADING') {
        setAddingToCart(event.data.isLoading)
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [setAddingToCart])

  // Handle add to cart
  const handleAddToCart = async () => {
    try {
      // Set loading state immediately - will continue indefinitely as frontend closes the window
      setAddingToCart(true)

      // Wait a moment to ensure any pending auto-saves are complete
      // The auto-save has a 500ms debounce, so we wait slightly longer
      await new Promise((resolve) => setTimeout(resolve, 600))

      // Check if there's an ongoing save and wait for it to complete
      const waitForSaveCompletion = async () => {
        let checkCount = 0
        const maxChecks = 20 // Max 10 seconds (20 * 500ms)

        while (checkCount < maxChecks) {
          const designStore = useDesignStore.getState()
          if (!designStore.isSaving) {
            break
          }
          await new Promise((resolve) => setTimeout(resolve, 500))
          checkCount++
        }
      }

      await waitForSaveCompletion()

      // Send message to parent window
      if (window.parent && window.parent !== window) {
        window.parent.postMessage(
          { type: 'DESIGN_ADDED_TO_CART', designId: design.designId },
          '*', // You might want to restrict this to specific origins in production
        )
      }
      // Note: Not resetting loading state here - frontend will close the window
    } catch (error) {
      console.error('Failed to add to cart:', error)
      // Only reset loading state on error
      setAddingToCart(false)
    }
  }

  // Handle image upload
  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file || !currentDesign) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showToast(intl.alerts.invalidImageFile, 'error')
      return
    }

    // Only JPEG and PNG are accepted server-side — block unsupported image
    // types (webp, gif, …) up front so we save the roundtrip and always show
    // a localized message.
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      showToast(intl.alerts.unsupportedImageType, 'error')
      return
    }

    // Validate file size (max 25MB)
    const maxSize = 25 * 1024 * 1024
    if (file.size > maxSize) {
      showToast(intl.alerts.imageSizeTooLarge, 'error')
      return
    }

    setIsUploading(true)
    setUploadProgress(0)

    try {
      // Request signed upload URL from backend
      const uploadData = await designApi.getUploadUrl(currentDesign.designId, {
        filename: file.name,
        contentType: file.type,
      })

      // Upload to S3
      await designApi.uploadToS3(uploadData.uploadUrl, file, (progress) => {
        console.log(`Upload progress: ${progress.toFixed(0)}%`)
        setUploadProgress(progress)
      })

      // Load image to get dimensions
      const img = new Image()
      img.onload = () => {
        // Convert pixel dimensions to mm
        const widthMm = pxToMm(img.width)
        const heightMm = pxToMm(img.height)

        // Calculate actual print dimensions after potential scaling
        let actualPrintWidthMm = widthMm
        let actualPrintHeightMm = heightMm

        // Check if image will be scaled to fit the design zone
        if (currentDesign) {
          const designZone = currentDesign.zones.design
          const maxWidth = designZone.width * 0.8 // 80% of design zone width
          const maxHeight = designZone.height * 0.8 // 80% of design zone height

          if (widthMm > maxWidth || heightMm > maxHeight) {
            // Image will be scaled down to fit
            const scaleX = maxWidth / widthMm
            const scaleY = maxHeight / heightMm
            const scale = Math.min(scaleX, scaleY)

            actualPrintWidthMm = widthMm * scale
            actualPrintHeightMm = heightMm * scale
          }
        }

        // Calculate DPI based on actual print dimensions
        const dpi = calculateImageDPI(
          img.width,
          img.height,
          actualPrintWidthMm,
          actualPrintHeightMm,
        )
        if (!isAcceptableImageQuality(dpi)) {
          setImageQualityWarning({
            show: true,
            imageName: file.name,
            dpi,
          })
        }

        if (replacingImageId) {
          // Replace existing image
          const element = elements[replacingImageId]
          if (element && element.type === 'image') {
            // Update the element with new image but keep the same position and size
            setElements({
              ...elements,
              [replacingImageId]: {
                ...element,
                src: uploadData.fileUrl,
                value: uploadData.fileUrl,
                // Keep existing dimensions and transforms
              },
            })
          }
          setReplacingImageId(null)
        } else {
          // Add new image to canvas
          addImageElement({
            src: uploadData.fileUrl,
            width: widthMm,
            height: heightMm,
            centerInDesignZone: true,
          })
        }
      }
      img.onerror = () => {
        const error = new Error(`Failed to load image: ${uploadData.fileUrl}`)
        Sentry.captureException(error, {
          tags: { component: 'DesignStudioEditor', operation: 'imageLoad' },
          extra: { fileName: file.name, fileUrl: uploadData.fileUrl },
        })
        showToast(intl.alerts.imageLoadFailed, 'error')
      }
      img.src = uploadData.fileUrl
    } catch (error) {
      console.error('Upload failed:', error)

      // Skip Sentry for the unsupported-content-type case — it's a user
      // validation error we already surface via a localized toast. Report
      // everything else (other 4xx, S3 failures, network errors) so we keep
      // visibility into real problems.
      const isUnsupportedContentType =
        error instanceof UploadUrlApiError && error.code === 'UNSUPPORTED_CONTENT_TYPE'

      if (!isUnsupportedContentType) {
        Sentry.captureException(error, {
          tags: { component: 'DesignStudioEditor', operation: 'imageUpload' },
          extra: {
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
            designId: currentDesign.designId,
          },
        })
      }

      const errorMessage = isUnsupportedContentType
        ? intl.alerts.unsupportedImageType
        : error instanceof Error
          ? error.message
          : intl.alerts.uploadFailed
      showToast(errorMessage, 'error')
    } finally {
      setIsUploading(false)
      setReplacingImageId(null)
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  // Initialize stores with design data
  useEffect(() => {
    // Set design in store using a more controlled approach
    const designStore = useDesignStore.getState()
    designStore.setSaveState({
      isSaving: false,
      lastSaved: null,
      saveError: null,
      retryAttempt: 0,
    })

    useDesignStore.setState({
      currentDesign: design,
      zoneOverrides: null,
      isLoading: false,
    })

    // Convert API elements to canvas store format
    const elementsMap: Record<string, any> = {}
    const imageLoadPromises: Promise<void>[] = []

    design.elements.forEach((element: any) => {
      // Determine default dimensions based on element type
      const defaultDimensions =
        element.type === 'asset'
          ? {
              width: 50,
              height: 50,
            } // Default 50mm x 50mm for assets
          : element.type === 'image'
            ? {
                width: 100, // Default 100mm for images (will be updated when loaded)
                height: 100,
              }
            : { width: 100, height: 30 } // Default for text

      // Determine layer based on element type
      const layerMap = {
        image: 0, // Bottom layer
        asset: 1, // Middle layer
        text: 2, // Top layer
      }

      const mappedElement = {
        id: element.elementId,
        type: element.type,
        position: {
          x: element.centerPosition.left,
          y: element.centerPosition.top,
        },
        dimensions: defaultDimensions,
        transform:
          (element.type === 'asset' || element.type === 'image') && element.transform
            ? (() => {
                const parsedTransform = transformOperations.fromCSSString(element.transform)
                // If the transform contains rotation (backward compatibility), use it
                // Otherwise, use the separate rotation field
                if (parsedTransform.rotation !== 0 && !element.rotation) {
                  return parsedTransform
                }
                // Use the separate rotation field (new approach)
                return {
                  ...parsedTransform,
                  rotation: element.rotation || 0,
                }
              })()
            : {
                rotation: element.rotation || 0,
                scaleX: 1,
                scaleY: 1,
                skewX: 0,
                skewY: 0,
              },
        locked: false,
        visible: true,
        layer: layerMap[element.type as keyof typeof layerMap] || 1,
        ...(element.type === 'text' && {
          content: element.value || '',
          fontFamily: element.fontFamily || 'Arial',
          fontSize: element.fontSize || 16,
          fontWeight: element.fontWeight || 400,
          fontItalic: element.fontItalic || false,
          textAlign: 'left',
          color: element.fontColor || '#000000',
        }),
        ...(element.type === 'asset' && {
          src: '', // Will be loaded asynchronously from assetId
          assetId: element.assetId,
          assetType: element.assetType,
          color: element.color,
          // Note: folderName and fileName not included - no longer used
        }),
        ...(element.type === 'image' && {
          value: element.value,
          src: element.value || 'https://via.placeholder.com/200x200', // Use value as src
          borderRadius: element.borderRadius || 0,
          filters: element.filters,
        }),
      }

      elementsMap[element.elementId] = mappedElement

      // For asset elements with assetId, load the URL asynchronously
      if (element.type === 'asset' && element.assetId) {
        const { elementId, assetId } = element

        const loadAssetUrl = new Promise<void>((resolve) => {
          assetRegistry
            .getAssetUrl(assetId)
            .then((url) => {
              if (url && elementsMap[elementId]) {
                elementsMap[elementId].src = url
              }
              resolve()
            })
            .catch((error) => {
              console.error(`Failed to load asset URL for ${assetId}:`, error)
              // No fallback - assetId is required
              resolve()
            })
        })

        imageLoadPromises.push(loadAssetUrl)
      }

      // For image elements, load the actual dimensions
      if (element.type === 'image' && element.value) {
        const imageUrl = element.value
        if (!imageUrl) return
        const { elementId } = element

        const loadImageDimensions = new Promise<void>((resolve) => {
          const img = new Image()
          img.onload = () => {
            // Convert pixel dimensions to mm
            const widthMm = pxToMm(img.width)
            const heightMm = pxToMm(img.height)

            // Update the element with actual dimensions
            if (elementsMap[elementId]) {
              // Set the base dimensions (unscaled)
              elementsMap[elementId].dimensions = {
                width: widthMm,
                height: heightMm,
              }

              // The transform (including scale) has already been loaded from the backend
              // and applied to the element, so the scaling will work correctly now
            }
            resolve()
          }
          img.onerror = () => {
            console.warn(`Failed to load image dimensions for ${imageUrl}`)
            resolve() // Resolve anyway to not block other images
          }
          img.src = imageUrl
        })

        imageLoadPromises.push(loadImageDimensions)
      }
    })

    // If there are images to load, wait for them before setting elements
    if (imageLoadPromises.length > 0) {
      // After all images load, set elements with actual dimensions
      Promise.all(imageLoadPromises).then(() => {
        setElements(elementsMap)
        // Notify that images are loaded
        setLoadingState({ imagesLoaded: true })
      })
    } else {
      // No images to load, set elements immediately
      setElements(elementsMap)
      // Notify that images are loaded (no images to load)
      setLoadingState({ imagesLoaded: true })
    }

    // Cleanup on unmount
    return () => {
      resetCanvas()
      resetDesign()
    }
  }, [design, setElements, resetCanvas, resetDesign, setLoadingState])

  // Determine if this is an engrave design
  const isEngraveDesign = currentDesign?.type === 'engrave'

  return (
    <div className="fixed inset-0 bg-white">
      <Toolbar
        onAddText={() => {
          const textDefaults = useTextDefaultsStore.getState().defaults
          // Check if defaults are from localStorage (user has saved fonts before)
          // or if they're just the hardcoded initialDefaults
          const hasStoredDefaults = localStorage.getItem('text-defaults-storage') !== null
          setEditingTextId(null)
          setTextContent('')
          // First-time use leaves the font empty; TextEditor auto-picks the first font
          // from the catalog once it loads, so the dropdown never sits on "Geen voorbeeld".
          setTextFont(hasStoredDefaults ? textDefaults.fontFamily : '')
          setTextFontWeight(400)
          setTextFontItalic(false)
          setTextSize(textDefaults.fontSize)
          setShowTextEditor(true)
        }}
        onChooseImage={() => openAssetPanel(currentDesign?.type || 'print')}
        onUploadPhoto={() => fileInputRef.current?.click()}
        onAddToCart={handleAddToCart}
        onShowHelp={startTour}
        isUploading={isUploading}
        isSaving={isSaving}
        isAddingToCart={isAddingToCart}
        isEditMode={isEditMode}
        showUploadPhoto={!isEngraveDesign}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png"
        onChange={handleImageUpload}
        className="hidden"
      />

      {/* Main content area with canvas and overlays */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Canvas - full width */}
        <InteractiveCanvas
          isMobile={isMobile}
          isDisabled={isAddingToCart}
          onTextEdit={(elementId) => {
            const element = elements[elementId]
            if (element && element.type === 'text') {
              setEditingTextId(elementId)
              setTextContent(element.content || '')
              setTextFont(element.fontFamily || 'Arial')
              setTextFontWeight(element.fontWeight || 400)
              setTextFontItalic(element.fontItalic || false)
              setTextSize(Math.round(element.fontSize || 16))
              setShowTextEditor(true)
            }
          }}
          onImageReplace={(elementId) => {
            setReplacingImageId(elementId)
            fileInputRef.current?.click()
          }}
          onImageEdit={(elementId) => {
            setEditingImageId(elementId)
            setShowImageEditor(true)
          }}
        />

        {/* Asset Panel - overlay */}
        <AssetPanelWrapper />

        {/* Upload Loading Overlay */}
        {isUploading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center">
            <div className="bg-white rounded-lg p-8 max-w-sm mx-4 text-center shadow-xl">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-nlt-green mx-auto mb-4"></div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{intl.uploadModal.title}</h3>
              <p className="text-gray-600">{intl.uploadModal.message}</p>

              {/* Progress bar */}
              <div className="mt-4">
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-nlt-green h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-500 mt-2">{uploadProgress.toFixed(0)}%</p>
              </div>
            </div>
          </div>
        )}

        {/* Text Editor Modal */}
        {showTextEditor && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) {
                setShowTextEditor(false)
                setEditingTextId(null)
              }
            }}
          >
            <TextEditor
              editingTextId={editingTextId}
              isMobile={isMobile}
              baseColor={currentDesign?.baseColor || '#000000'}
              initialTextContent={textContent}
              initialTextFont={textFont}
              initialTextFontWeight={textFontWeight}
              initialTextFontItalic={textFontItalic}
              initialTextSize={textSize}
              onSave={async (update) => {
                if (update.textContent.trim()) {
                  if (editingTextId) {
                    // Update existing text
                    await updateTextContent(editingTextId, update.textContent)
                    await updateTextFont(
                      editingTextId,
                      update.textFont,
                      update.textFontWeight,
                      update.textFontItalic,
                    )
                    await updateTextFontSize(editingTextId, update.textSize)
                  } else {
                    // Save the current settings as new defaults before creating
                    useTextDefaultsStore.getState().setDefaults({
                      fontFamily: update.textFont,
                      fontSize: Math.round(update.textSize),
                      // fontColor will use the design's baseColor or default
                    })

                    // Create new text element (will use the defaults we just set)
                    await addTextElement(true)
                    // Get the newly created element
                    const storeState = useCanvasStore.getState()
                    const elementIds = Object.keys(storeState.elements)
                    const lastElementId = elementIds[elementIds.length - 1]
                    if (lastElementId) {
                      // Only update content since font and size are already set from defaults
                      await updateTextContent(lastElementId, update.textContent)
                    }
                  }
                  setShowTextEditor(false)
                  setEditingTextId(null)
                }
              }}
              onCancel={() => {
                setShowTextEditor(false)
                setEditingTextId(null)
              }}
            />
          </div>
        )}

        {/* Image Edit Panel */}
        {showImageEditor && editingImageId && elements[editingImageId]?.type === 'image' && (
          <ImageEditPanel
            element={elements[editingImageId]}
            onUpdate={(updates) => {
              updateImageElement(editingImageId, updates)
            }}
            onClose={() => {
              setShowImageEditor(false)
              setEditingImageId(null)
            }}
          />
        )}

        {/* Image Quality Warning */}
        <ImageQualityWarning
          show={imageQualityWarning.show}
          imageName={imageQualityWarning.imageName}
          dpi={imageQualityWarning.dpi}
          onDismiss={() => setImageQualityWarning({ show: false, imageName: '', dpi: 0 })}
          isMobile={isMobile}
        />

        {/* Demo FloatingActionBar for Onboarding Tour Step 3 */}
        {shouldShowTour && (
          <div className="floating-action-bar fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50 pointer-events-none">
            <FloatingActionBar
              onHorizontalCenter={() => {}}
              onVerticalCenter={() => {}}
              onCenterInDesign={() => {}}
              onDelete={() => {}}
              onSendToBack={() => {}}
              onFlipHorizontal={() => {}}
              showFlipHorizontalButton={true}
              onColorChange={() => {}}
              showColorButton={true}
              onTextEdit={() => {}}
              showTextEditButton={true}
              onImageReplace={() => {}}
              showImageReplaceButton={true}
              className="pointer-events-none opacity-90"
            />
          </div>
        )}

        {/* Onboarding Tour */}
        <OnboardingTour
          enabled={shouldShowTour}
          onComplete={markTourCompleted}
          onSkip={markTourCompleted}
        />

        {/* AI Chat Toggle Button */}
        {isAIChatbotEnabled && !isAIChatOpen && !isMobile && (
          <button
            onClick={() => setIsAIChatOpen(true)}
            className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105"
            title="AI Design Assistent"
          >
            <Sparkles className="w-5 h-5" />
            <span className="font-medium">AI Assistent</span>
          </button>
        )}

        {/* AI Chat Panel */}
        {isAIChatbotEnabled && (
          <AIChatPanel isOpen={isAIChatOpen} onClose={() => setIsAIChatOpen(false)} />
        )}
      </div>
    </div>
  )
}
